import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, Phone } from "lucide-react";
import { COMPANY, NAV } from "@/data/site";
import { useQuote } from "@/components/QuoteModal";
import { track } from "@/lib/analytics";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const SoonPill = () => (
  <span className="ml-1.5 rounded-sm bg-cts-redlight px-1.5 py-0.5 font-heading font-bold text-[8.5px] tracking-[0.1em] text-cts-red align-middle whitespace-nowrap">
    COMING SOON
  </span>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const { openQuote } = useQuote();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 bg-white transition-all duration-300 ${
          scrolled ? "shadow-[0_6px_24px_-12px_rgba(23,27,30,0.25)]" : "border-b border-cts-line"
        }`}
      >
        <div
          className={`container-site flex items-center justify-between transition-all duration-300 ${
            scrolled ? "h-16" : "h-20"
          }`}
        >
          <Link to="/" aria-label="Cherians Tech Solutions home" data-testid="header-logo" className="shrink-0">
            <img src="/cts-logo.png" alt="Cherians Tech Solutions logo" className={`w-auto transition-all duration-300 ${scrolled ? "h-10" : "h-12"}`} />
          </Link>

          <nav aria-label="Primary" className="hidden xl:flex items-center">
            {NAV.map((item) =>
              item.children ? (
                <div
                  key={item.label}
                  className="relative group"
                  onMouseEnter={() => setOpenDropdown(item.label)}
                  onMouseLeave={() => setOpenDropdown((cur) => (cur === item.label ? null : cur))}
                  onFocus={() => setOpenDropdown(item.label)}
                  onBlur={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget)) {
                      setOpenDropdown((cur) => (cur === item.label ? null : cur));
                    }
                  }}
                >
                  <NavLink
                    to={item.to}
                    aria-haspopup="true"
                    aria-expanded={openDropdown === item.label}
                    data-testid={`nav-link-${slug(item.label)}`}
                    className={({ isActive }) =>
                      `flex items-center gap-1 px-1.5 py-2 font-heading font-bold text-[12px] xl:text-[12.5px] uppercase tracking-wide whitespace-nowrap transition-colors ${
                        isActive ? "text-cts-red" : "text-cts-charcoal hover:text-cts-red"
                      }`
                    }
                  >
                    {item.label}
                    <ChevronDown size={13} className="transition-transform duration-200 group-hover:rotate-180" />
                  </NavLink>
                  <div className="absolute left-0 top-full pt-1 invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 group-focus-within:visible group-focus-within:opacity-100 transition-all duration-200">
                    <div className="w-72 bg-white border border-cts-line rounded-lg shadow-xl py-2">
                      {item.children.map((child) =>
                        child.quote ? (
                          <button
                            key={child.label}
                            onClick={() => openQuote(child.quote)}
                            data-testid={`nav-sublink-${slug(child.label)}`}
                            className="flex w-full items-center text-left px-4 py-2.5 text-sm font-body text-cts-charcoal hover:bg-cts-soft hover:text-cts-red transition-colors"
                          >
                            {child.label}
                            {child.badge && <SoonPill />}
                          </button>
                        ) : (
                          <Link
                            key={child.label}
                            to={child.to}
                            data-testid={`nav-sublink-${slug(child.label)}`}
                            className="flex items-center px-4 py-2.5 text-sm font-body text-cts-charcoal hover:bg-cts-soft hover:text-cts-red transition-colors"
                          >
                            {child.label}
                            {child.badge && <SoonPill />}
                          </Link>
                        )
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <NavLink
                  key={item.label}
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={`nav-link-${slug(item.label)}`}
                  className={({ isActive }) =>
                    `px-1.5 py-2 font-heading font-bold text-[12px] xl:text-[12.5px] uppercase tracking-wide whitespace-nowrap transition-colors ${
                      isActive ? "text-cts-red" : "text-cts-charcoal hover:text-cts-red"
                    }`
                  }
                >
                  {item.label}
                  {item.badge && <SoonPill />}
                </NavLink>
              )
            )}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={COMPANY.phoneHref}
              onClick={() => track("phone_click", { location: "header" })}
              data-testid="header-call-link"
              className="hidden 2xl:flex items-center gap-2 whitespace-nowrap font-heading font-bold text-sm text-cts-charcoal hover:text-cts-red transition-colors"
            >
              <Phone size={17} strokeWidth={2} />
              {COMPANY.phone}
            </a>
            <button
              onClick={() => openQuote()}
              data-testid="header-quote-btn"
              className="btn-primary hidden xl:inline-flex !min-h-[44px] !py-2.5 !px-4 text-[13px] whitespace-nowrap shrink-0"
            >
              GET A QUOTE
            </button>
            <button
              className="xl:hidden p-2 text-cts-ink"
              aria-label="Open menu"
              aria-expanded={mobileOpen}
              data-testid="mobile-menu-btn"
              onClick={() => setMobileOpen(true)}
            >
              <Menu size={26} />
            </button>
          </div>
        </div>
      </header>

      {mobileOpen && (
        <div className="fixed inset-0 z-[70] bg-white flex flex-col xl:hidden" data-testid="mobile-menu">
          <div className="container-site h-20 flex items-center justify-between border-b border-cts-line">
            <img src="/cts-logo.png" alt="Cherians Tech Solutions logo" className="h-11 w-auto" />
            <button className="p-2 text-cts-ink" aria-label="Close menu" data-testid="mobile-menu-close-btn" onClick={() => setMobileOpen(false)}>
              <X size={26} />
            </button>
          </div>
          <nav aria-label="Mobile" className="flex-1 overflow-y-auto container-site py-6">
            {NAV.map((item) => (
              <div key={item.label} className="border-b border-cts-line">
                <div className="flex items-center">
                  <NavLink
                    to={item.to}
                    end={item.to === "/"}
                    data-testid={`mobile-nav-link-${slug(item.label)}`}
                    className={({ isActive }) =>
                      `block py-3 font-heading font-bold text-lg ${isActive ? "text-cts-red" : "text-cts-ink"}`
                    }
                  >
                    {item.label}
                  </NavLink>
                  {item.badge && <SoonPill />}
                </div>
                {item.children && (
                  <div className="pb-3 pl-4 space-y-1">
                    {item.children.map((child) =>
                      child.quote ? (
                        <button
                          key={child.label}
                          data-testid={`mobile-nav-sublink-${slug(child.label)}`}
                          className="flex w-full items-center text-left py-1.5 text-[15px] text-cts-charcoal"
                          onClick={() => {
                            setMobileOpen(false);
                            openQuote(child.quote);
                          }}
                        >
                          {child.label}
                          {child.badge && <SoonPill />}
                        </button>
                      ) : (
                        <Link key={child.label} to={child.to} data-testid={`mobile-nav-sublink-${slug(child.label)}`} className="flex items-center py-1.5 text-[15px] text-cts-charcoal">
                          {child.label}
                          {child.badge && <SoonPill />}
                        </Link>
                      )
                    )}
                  </div>
                )}
              </div>
            ))}
          </nav>
          <div className="container-site py-5 border-t border-cts-line">
            <button
              data-testid="mobile-menu-quote-btn"
              className="btn-primary w-full"
              onClick={() => {
                setMobileOpen(false);
                openQuote();
              }}
            >
              GET A QUOTE
            </button>
          </div>
        </div>
      )}
    </>
  );
};
